/**
 * Password Link Component
 *
 * This component allows users who signed in with Google SSO via Firebase
 * to link an email/password authentication method to their account.
 * This enables them to sign in with either Google or email/password.
 */

import React, { useState, useEffect } from 'react';
import {
  linkWithCredential,
  EmailAuthProvider,
  User,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';

interface PasswordLinkProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const PasswordLink: React.FC<PasswordLinkProps> = ({ onSuccess, onError }) => {
  const { user: backendUser } = useAuth();
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [hasPassword, setHasPassword] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check Firebase auth state and provider data
  useEffect(() => {
    if (!isFirebaseConfigured() || !auth) {
      setIsChecking(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
        // Check if email/password is already linked
        const providers = user.providerData.map((provider) => provider.providerId);
        const hasEmailPassword = providers.includes('password');
        setHasPassword(hasEmailPassword);
      } else {
        setFirebaseUser(null);
        setHasPassword(false);
      }
      setIsChecking(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLinkPassword = async () => {
    setError(null);

    // Validation
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!firebaseUser) {
      setError('You must be signed in with Firebase to link a password. Please sign in with Google via Firebase first.');
      onError?.(new Error('No Firebase user found'));
      return;
    }

    if (!backendUser?.email) {
      setError('Unable to find your email address. Please contact support.');
      onError?.(new Error('No email found'));
      return;
    }

    setIsLoading(true);

    try {
      // Create email/password credential
      const credential = EmailAuthProvider.credential(backendUser.email, password);

      // Link the credential to the current Firebase user
      await linkWithCredential(firebaseUser, credential);

      setHasPassword(true);
      setShowForm(false);
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Password linking error:', err);
      let errorMessage = 'Failed to link password';

      // Handle specific Firebase errors
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already associated with a different account.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (err.code === 'auth/requires-recent-login') {
        errorMessage = 'For security, please sign out and sign back in with Google, then try linking your password again.';
      } else if (err.code === 'auth/credential-already-in-use') {
        errorMessage = 'This password is already linked to your account.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show anything if Firebase is not configured
  if (!isFirebaseConfigured()) {
    return null;
  }

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // Only show if user has Firebase UID (signed in via Firebase)
  if (!backendUser?.firebase_uid) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Password linking unavailable</p>
            <p className="text-gray-600 dark:text-gray-400">To link a password, you must first sign in with Google via Firebase authentication.</p>
          </div>
        </div>
      </div>
    );
  }

  // If user has firebase_uid but no active Firebase session, they need to re-authenticate
  if (!firebaseUser) {
    return (
      <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 dark:text-amber-300 mb-1">Re-authentication required</p>
            <p className="text-amber-800 dark:text-amber-200">To link a password, please sign out and sign back in with Google via Firebase. This ensures your account is properly authenticated.</p>
          </div>
        </div>
      </div>
    );
  }

  // If password is already linked, show success state
  if (hasPassword) {
    return (
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
        <div className="flex items-center">
          <Check className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-primary-900 dark:text-primary-300">Email/Password authentication is linked</p>
            <p className="text-xs text-primary-700 dark:text-primary-200 mt-1">
              You can sign in using either Google or email/password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show form to link password
  if (!showForm) {
    return (
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <Lock className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2 flex-shrink-0" />
              <p className="text-sm font-medium text-primary-900 dark:text-primary-300">Add Email/Password Sign-In</p>
            </div>
            <p className="text-xs text-primary-700 dark:text-primary-200 mt-1">
              Link a password to your account so you can sign in with email and password in addition to Google.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="ml-4 inline-flex items-center px-3 py-1.5 border border-primary-300 dark:border-primary-600 text-sm font-medium rounded-lg text-primary-700 dark:text-primary-300 bg-white dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            Add Password
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Lock className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Link Email/Password</h3>
        </div>
        <button
          onClick={() => {
            setShowForm(false);
            setError(null);
            setPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowConfirmPassword(false);
          }}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Set a password for <span className="font-medium text-gray-900 dark:text-gray-100">{backendUser.email}</span> to enable email/password sign-in.
        </p>
      </div>

      <div>
        <label htmlFor="link-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Password
        </label>
        <div className="relative">
          <input
            id="link-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-3 py-2 pr-10 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Enter your password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={isLoading}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Password must be at least 6 characters.</p>
      </div>

      <div>
        <label htmlFor="link-confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Confirm Password
        </label>
        <div className="relative">
          <input
            id="link-confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-3 py-2 pr-10 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Confirm your password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
            tabIndex={-1}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            disabled={isLoading}
          >
            {showConfirmPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          onClick={handleLinkPassword}
          disabled={isLoading || !password || !confirmPassword}
          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Linking...
            </>
          ) : (
            'Link Password'
          )}
        </button>
        <button
          onClick={() => {
            setShowForm(false);
            setError(null);
            setPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowConfirmPassword(false);
          }}
          disabled={isLoading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PasswordLink;

